import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { visibleNavItems } from './app-nav';
import { IdentityService } from './core/auth/identity.service';
import { AdminUserInfo } from './core/auth/models/admin-user-info';
import {
    AdminApiEnvironment,
    AdminApiEnvironmentKind,
    apiEnvironmentLabel,
    apiEnvironmentTone,
} from './core/country/api-environment.model';
import { CountryDomainService } from './core/country/country-domain.service';
import { CountryCode } from './core/country/country.model';

interface AppState {
    isSidenavOpen: boolean;
    user: AdminUserInfo | null;
    /** Mirrors `CountryDomainService.activeCountry` so country-gated nav entries react to switches. */
    activeCountry: CountryCode;
    /**
     * Mirrors `CountryDomainService.apiEnvironment()` — the one fact behind both the toolbar indicator
     * and the prod-only nav gating. Held in state rather than read straight from the service in a
     * computed because a `withComputed` may only read store signals and call pure functions; it is set
     * once in `onInit`, since the value derives from the host name and cannot change without a reload.
     *
     * `isProdDeployment` is *derived* from it below rather than stored next to it: two copies of one
     * fact can disagree, which is exactly what `apiEnvironment()` exists to prevent one layer down.
     */
    apiEnvironment: AdminApiEnvironment;
    /**
     * The API origin of the *active* country — the indicator's tooltip. Unlike the field above this one
     * does change at runtime, so it is refreshed by `watchCountryRx` on every country switch.
     */
    apiBaseUrl: string;
}

const initialState: AppState = {
    isSidenavOpen: true,
    user: null,
    activeCountry: CountryCode.CA,
    apiEnvironment: { kind: AdminApiEnvironmentKind.Production },
    apiBaseUrl: '',
};

export const AppStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    // Split from the block below only because `visibleNav` consumes `isProdDeployment`: within one
    // `withComputed` the store does not yet carry its siblings.
    withComputed((store) => ({
        isProdDeployment: computed(
            () => store.apiEnvironment().kind === AdminApiEnvironmentKind.Production,
        ),
        apiEnvironmentLabel: computed(() => apiEnvironmentLabel(store.apiEnvironment())),
        apiEnvironmentTone: computed(() => apiEnvironmentTone(store.apiEnvironment())),
    })),
    withComputed((store) => ({
        isAuthenticated: computed(() => store.user() !== null),
        visibleNav: computed(() => visibleNavItems(
            store.user()?.role,
            store.activeCountry(),
            store.isProdDeployment(),
        )),
    })),
    withMethods((
        store,
        identityService = inject(IdentityService),
        countryDomainService = inject(CountryDomainService)
    ) => ({
        toggleSidenav(): void {
            patchState(store, s => ({ isSidenavOpen: !s.isSidenavOpen }));
        },
        openSidenav(): void {
            patchState(store, { isSidenavOpen: true });
        },
        closeSidenav(): void {
            patchState(store, { isSidenavOpen: false });
        },
        watchAuthRx: rxMethod<void>(pipe(
            switchMap(() => identityService.stateChanges),
            tap(state => patchState(store, { user: state.user })),
        )),
        watchCountryRx: rxMethod<void>(pipe(
            switchMap(() => countryDomainService.countryChanges$),
            tap(country => patchState(store, {
                activeCountry: country,
                apiBaseUrl: countryDomainService.apiBaseUrl(country),
            })),
        )),
    })),
    withHooks((
        store,
        countryDomainService = inject(CountryDomainService)
    ) => ({
        onInit: () => {
            // Set before the first render: `AppComponent` injecting the root store is what runs this.
            patchState(store, { apiEnvironment: countryDomainService.apiEnvironment() });
            store.watchAuthRx();
            // `countryChanges$` is a BehaviorSubject, so this also seeds `apiBaseUrl` synchronously.
            store.watchCountryRx();
        },
    })),
);
